// Reusable FilterBar component for consistent filter UI across the application
// Based on the transaction filter bar design with Apple-inspired minimalist styling
// 
// Usage:
//   <FilterBar filters={[
//     { type: 'select', label: 'Status', value: status, onChange: setStatus, options: [...] },
//     { type: 'react-select', label: 'Branch', value: branch, onChange: setBranch, options: [...] },
//   ]} />

import React from "react";
import Select from "react-select";

/**
 * FilterBar Component
 * @param {Array} filters - Array of filter configurations
 * @param {ReactNode} extraActions - Additional buttons/actions to display at the end (e.g., view toggles)
 */
function FilterBar({ filters = [], extraActions = null }) {
    // Shared styles for react-select
    const reactSelectStyles = {
        control: (base) => ({
            ...base,
            background: '#f6f6f8',
            border: 'none',
            boxShadow: 'none',
            minHeight: 36,
            borderRadius: 10,
            fontSize: 15,
        }),
        menu: (base) => ({ ...base, borderRadius: 10, fontSize: 15, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            background: state.isSelected ? '#e5e5ea' : state.isFocused ? '#f0f0f3' : '#fff',
            color: '#222',
            borderRadius: 8,
            fontSize: 15,
        }),
    };

    return (
        <div className="flex flex-wrap gap-3 mb-1 items-end bg-[#f6f6f8] p-2 rounded-xl shadow-md">
            {filters.map((filter, index) => {
                const { type, label, value, onChange, options, placeholder, isSearchable = false, minWidth = '120px', hide = false, className = '', min, max } = filter;

                // Skip hidden filters
                if (hide) return null;

                // Native select dropdown
                if (type === 'select') {
                    return (
                        <div key={index} className={`flex flex-col ${className}`} style={{ minWidth }}>
                            <label className="text-[13px] font-semibold mb-1 text-gray-700 pl-1">{label}</label>
                            <select
                                className="bg-[#f6f6f8] rounded-xl px-3 py-2 text-[15px] border-0 focus:ring-2 focus:ring-primary"
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                            >
                                {options.map((opt, idx) => (
                                    <option key={idx} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                }

                // React-select dropdown (for searchable/complex options)
                if (type === 'react-select') {
                    return (
                        <div key={index} className={`flex flex-col ${className}`} style={{ minWidth }}>
                            <label className="text-[13px] font-semibold mb-1 text-gray-700 pl-1">{label}</label>
                            <Select
                                classNamePrefix="react-select"
                                styles={reactSelectStyles}
                                options={options}
                                value={value}
                                onChange={(opt) => onChange(opt ? opt.value : '')}
                                isSearchable={isSearchable}
                                placeholder={placeholder || label}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    );
                }

                if (type === 'date') {
                    return (
                        <div key={index} className={`flex flex-col ${className}`} style={{ minWidth }}>
                            <label className="text-[13px] font-semibold mb-1 text-gray-700 pl-1">{label}</label>
                            <input
                                type="date"
                                className="bg-[#f6f6f8] rounded-xl px-3 py-2 text-[15px] border-0 focus:ring-2 focus:ring-primary"
                                value={value || ''}
                                onChange={(e) => onChange(e.target.value)}
                                min={min}
                                max={max}
                            />
                        </div>
                    );
                }

                return null;
            })}

            {/* Extra actions (e.g., view toggles) */}
            {extraActions && (
                <div className="flex ml-auto gap-2">
                    {extraActions}
                </div>
            )}
        </div>
    );
}

export default FilterBar;
